import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { DateTime } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchEmployeeDeclarationEventByEidAndDeclarationYear } from "../../api/employee";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import PageLoading from "../../components/PageLoading";
import ConfirmationDialog from "../../components/fundamentals/ConfirmationDialog.jsx";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { logout } from "../../util/authentication";
import {
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  EMP_DECLARATION_CREATED_BY_TYPES,
  FEATURES,
  HOME_PATH,
  IAM_CONTRACT,
} from "../../util/constants";
import {
  validateBlockWeeksCount,
  validateDATWeeksCount,
  validateDatAndBlockWeeksWithTotalBalances,
} from "../../util/declarations/declarationValidations";
import {
  DECLARATION_ERROR_MESSAGE_DIALOG,
  DECLARATION_FORM_CANCEL_MESSAGE,
  DECLARATION_SUCCESS_MESSAGE_DIALOG,
  DECLARATION_YEAR_SELECTION_CHANGE,
  LAST_UPDATED,
  LAST_UPDATED_TIMESTAMP,
  MY_DECLARATIONS_IAM_TITLE,
} from "../../util/messageConstants.js";

import {
  getLastUpdatedTimeString,
  getStationTimeZoneLuxonDateTimeFromIsoDateTimeString,
  getTimeZoneFromSwaStation,
} from "../../util/timeZoneService";
import StarsSelectInput from "../fundamentals/StarsSelectInput";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import { addEmpDeclarationEvent, fetchDeclarationWindowDates } from "../../api/employee";
import {
  buildDeclarationEvent,
  getFormattedProjectedVacationBalance,
  getProjectedVacationBalanceWeeks,
  isNextDeclarationYearReadyToDisplay,
} from "../../util/declarations/declarationUtils";
import { NO_ACCESS_TO_PAGE_MESSAGE } from "../../util/messageConstants";
import { getUnionContractGivenJobCode, getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import ConfirmButton from "../fundamentals/ConfirmButton";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../fundamentals/SuccessDialog";
import DeclarationTimer from "./DeclarationTimer";
import { HIDE_DCLRTN_INFO_FOR_EMP_VIEW_YEAR } from "../../util/declarations/declarationConstants";

const useStyles = makeStyles((theme) => ({
  lastSubmitted: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  inputText: {
    width: "160px",
    textAlign: "left",
    paddingRight: "1rem",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  declarationInput: {
    paddingLeft: "1rem",
  },
  filterInputs: {
    width: "115px",
    textAlign: "left",
    backgroundColor: theme.palette.secondary.swaWhite,
  },
  declarationScroll: {
    width: "100%",
    height: "90vh",
    overflowY: "scroll",
    position: "absolute",
    textAlign: "left",
  },
}));

const MyDeclarationsPageIam = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [datWeeks, setDatWeeks] = useState("");
  const [datWeeksErrors, setDatWeeksErrors] = useState("");
  const [blockWeeks, setBlockWeeks] = useState("");
  const [blockWeeksErrors, setBlockWeeksErrors] = useState("");
  const [initialDatWeeks, setInitialDatWeeks] = useState("");
  const [initialBlockWeeks, setInitialBlockWeeks] = useState("");
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState("");
  const [declarationWindowDates, setDeclarationWindowDates] = useState([null, null]);
  const [empDeclarationData, setEmpDeclarationData] = useState(null);
  const [projectedVacationBalance, setProjectedVacationBalance] = useState("");
  const breadcrumbsLink = [HOME_PATH];
  const employeeBreadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_DECLARATIONS];
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const selectedYearRef = useRef("");
  const [declarationVersion, setDeclarationVersion] = useState("");
  const [isSubmitResponseDialogOpen, setIsSubmitResponseDialogOpen] = useState(false);
  const [saveErrors, setSaveErrors] = useState("");
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);

  // redirect state
  const location = useLocation();
  const [hasAccessToViewPage, setHasAccessToViewPage] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else if (declarationWindowDates[0] === null || declarationWindowDates[1] === null) {
      getDeclarationWindowDates();
    } else {
      setIsPageLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    //Automatically select declaration year from path params
    if (isFirstLoad && declarationWindowDates[0] && declarationWindowDates[1] && pathNameHasYear(location.pathname)) {
      const yearFromPath = location.pathname.split("/")[2];
      const allYearOptions = getDeclarationYearAllOptions();
      if (allYearOptions.includes(yearFromPath)) {
        setSelectedYear(yearFromPath);
        selectedYearRef.current = yearFromPath;
      } else {
        // given year from path is not one of the past valid 3 years so, do not have access
        setHasAccessToViewPage(false);
      }
      setIsFirstLoad(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstLoad, declarationWindowDates]);

  const pathNameHasYear = (urlPathname) => {
    const yearFound = /\d{4}/.test(urlPathname);
    return yearFound;
  };

  useEffect(() => {
    let currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(userSession.homeStation));
    if (selectedYear !== "" && selectedYear !== HIDE_DCLRTN_INFO_FOR_EMP_VIEW_YEAR) {
      fetchEmployeeDeclarationInfo(userSession.eid, selectedYear);
      if (
        currentDateTime.year + 1 === parseInt(selectedYear) &&
        currentDateTime > declarationWindowDates[0] &&
        currentDateTime < declarationWindowDates[1]
      ) {
        setIsReadOnly(false);
      } else {
        setIsReadOnly(true);
      }
    } else {
      setIsReadOnly(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const fetchEmployeeDeclarationInfo = async (eid, selectedYear) => {
    setIsPageLoading(true);
    fetchEmployeeDeclarationEventByEidAndDeclarationYear(eid, selectedYear)
      .then((res) => res.json())
      .then((response) => {
        if (!response.message) {
          if (response.updateType !== "DELETE") {
            setEmpDeclarationData(response);
            setLastUpdatedByFirstName(response.modifiedByFirstName || "");
            setLastUpdatedByLastName(response.modifiedByLastName || "");
            setInitialDatWeeks(response.datWeeksCount || "");
            setInitialBlockWeeks(response.blockWeeksCount || "");
            setDatWeeks(response.datWeeksCount || "");
            setBlockWeeks(response.blockWeeksCount || "");
            setDeclarationVersion(response.v);
            setProjectedVacationBalance(response.projectedVacationBalance || "");
          } else {
            setLastUpdatedByFirstName("");
            setLastUpdatedByLastName("");
            setDeclarationVersion(response.v);
            setDatWeeks("");
            setBlockWeeks("");
            setInitialDatWeeks("");
            setInitialBlockWeeks("");
            setEmpDeclarationData(null);
            setProjectedVacationBalance("");
          }
        }
      })
      .catch((err) => {
        setLastUpdatedByFirstName("");
        setLastUpdatedByLastName("");
        setEmpDeclarationData(null);
        console.error(err);
      });

    setIsPageLoading(false);
  };

  const getDeclarationWindowDates = () => {
    setIsPageLoading(true);
    fetchDeclarationWindowDates()
      .then((res) => res.json())
      .then((result) => {
        if (result?.response) {
          const declarationWindowStart = getStationTimeZoneLuxonDateTimeFromIsoDateTimeString(
            result.response.declarationWindowStart
          );
          const declarationWindowEnd = getStationTimeZoneLuxonDateTimeFromIsoDateTimeString(
            result.response.declarationWindowEnd
          );
          setDeclarationWindowDates([declarationWindowStart, declarationWindowEnd]);
          let currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(userSession.homeStation));
          // select the year only when path param doesn't have it
          if (!pathNameHasYear(location.pathname)) {
            if (currentDateTime > declarationWindowStart && currentDateTime < declarationWindowEnd) {
              setSelectedYear((currentDateTime.year + 1).toString());
              selectedYearRef.current = (currentDateTime.year + 1).toString();
            } else {
              setSelectedYear(currentDateTime.year.toString());
              selectedYearRef.current = currentDateTime.year.toString();
            }
          }
          setIsPageLoading(false);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        setIsPageLoading(false);
        console.error(`Bid Error: %j`, err);
      });
  };

  const getDeclarationYearAllOptions = () => {
    let declarationYearAllOptions = [];
    let currYear = DateTime.now().setZone(getTimeZoneFromSwaStation(userSession.homeStation)).year;
    for (let year = currYear - 3; year <= currYear; year++) {
      if (year > 2025) {
        declarationYearAllOptions.push(year.toString());
      }
    }
    if (
      declarationWindowDates[0] !== null &&
      isNextDeclarationYearReadyToDisplay(userSession.homeStation, declarationWindowDates[0])
    ) {
      const nextYear = (currYear + 1).toString();
      if (!declarationYearAllOptions.includes(nextYear)) {
        declarationYearAllOptions.push(nextYear);
      }
    }
    return declarationYearAllOptions;
  };

  const handleDeclarationYearChange = (event) => {
    const selectedYear = event.target.value;
    selectedYearRef.current = selectedYear;
    if (!isReadOnly) {
      setIsWarningDialogOpen(true);
    } else {
      setSelectedYear(selectedYear);
      resetState();
    }
  };

  const handleClickCancelDialogOpen = () => {
    setIsExitDialogOpen(true);
  };

  const handleDATWeeksEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleDATWeeksBlur(event);
    }
  };

  const handleDATWeeksChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/[^0-9]/g, "");
    setDatWeeks(sanitizedInput);
    setDatWeeksErrors("");
  };

  const handleDATWeeksBlur = () => {
    const datWeekErrors = validateDATWeeksCount(datWeeks);
    if (datWeekErrors === "") {
      setDatWeeksErrors("");
    } else {
      setDatWeeksErrors(datWeekErrors);
    }
    const blockWeekErrors = validateBlockWeeksCount(blockWeeks);
    if (blockWeekErrors === "") {
      setBlockWeeksErrors("");
    } else {
      setBlockWeeksErrors(blockWeekErrors);
    }
  };

  const handleBlockWeeksEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleBlockWeeksBlur(event);
    }
  };

  const handleBlockWeeksChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/[^0-9]/g, "");
    setBlockWeeks(sanitizedInput);
    setBlockWeeksErrors("");
  };

  const handleBlockWeeksBlur = () => {
    const blockWeekErrors = validateBlockWeeksCount(blockWeeks);
    if (blockWeekErrors === "") {
      setBlockWeeksErrors("");
    } else {
      setBlockWeeksErrors(blockWeekErrors);
    }
    const datWeekErrors = validateDATWeeksCount(datWeeks);
    if (datWeekErrors === "") {
      setDatWeeksErrors("");
    } else {
      setDatWeeksErrors(datWeekErrors);
    }
  };

  const isDeclarationChanged = () => {
    return datWeeks !== initialDatWeeks || blockWeeks !== initialBlockWeeks;
  };

  const isFormValidationSuccess = () => {
    const datWeeksValidationErrors = validateDATWeeksCount(datWeeks);
    const blockWeeksValidationErrors = validateBlockWeeksCount(blockWeeks);
    const projectedVacationBalanceWeeks = getProjectedVacationBalanceWeeks(
      IAM_CONTRACT,
      userSession.isFTOrPT,
      projectedVacationBalance
    );
    const validateWithTotalBalanceErrors = validateDatAndBlockWeeksWithTotalBalances(
      datWeeks,
      blockWeeks,
      projectedVacationBalanceWeeks
    );
    if (datWeeksValidationErrors !== "") {
      setDatWeeksErrors(datWeeksValidationErrors);
    }
    if (blockWeeksValidationErrors !== "") {
      setBlockWeeksErrors(blockWeeksValidationErrors);
    }
    if (validateWithTotalBalanceErrors !== "") {
      setDatWeeksErrors(validateWithTotalBalanceErrors);
      setBlockWeeksErrors(validateWithTotalBalanceErrors);
    }
    return (
      datWeeksValidationErrors === "" && blockWeeksValidationErrors === "" && validateWithTotalBalanceErrors === ""
    );
  };

  const handleClickSubmitDialogOpen = () => {
    if (!isFormValidationSuccess()) {
      return;
    } else {
      setIsSubmitDialogOpen(true);
    }
  };

  const resetState = () => {
    setDeclarationVersion("");
    setSaveErrors("");
    setDatWeeks("");
    setInitialDatWeeks("");
    setDatWeeksErrors("");
    setBlockWeeks("");
    setInitialBlockWeeks("");
    setBlockWeeksErrors("");
    setEmpDeclarationData(null);
    setLastUpdatedByFirstName("");
    setLastUpdatedByLastName("");
    if (pathNameHasYear(location.pathname)) {
      props.history.push(`/${FEATURES.MY_DECLARATIONS}`);
    }
  };

  const handleSubmitClicked = () => {
    if (!isFormValidationSuccess()) {
      setIsSubmitDialogOpen(false);
      setIsSubmitLoading(false);
      return;
    }
    const payload = buildDeclarationEvent(
      {
        createdBy: EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_EMP,
        v: declarationVersion,
        eid: userSession.eid,
        station: userSession.homeStation,
        unionContract: getUnionContractGivenJobCode(userSession.jobCode),
        updateType: declarationVersion ? "UPDATE" : "CREATE",
        workgroup: getWorkgroupGivenJobCode(userSession.jobCode),
        year: selectedYear,
        blockWeeksCount: blockWeeks,
        datWeeksCount: datWeeks,
        isAdminCreated: false,
      },
      userSession
    );

    setIsSubmitLoading(true);
    setSaveErrors("");

    addEmpDeclarationEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        if (result.error || result.message) {
          console.error("Error");
          setIsSubmitLoading(false);
          setIsSubmitDialogOpen(false);
          setIsSubmitResponseDialogOpen(true);
          setSaveErrors(result.error);
        } else {
          if (result.pk) {
            setIsSubmitLoading(false);
            setIsSubmitDialogOpen(false);
            setIsSubmitResponseDialogOpen(true);
          }
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsSubmitLoading(false);
        setIsSubmitDialogOpen(false);
        setIsSubmitResponseDialogOpen(true);
        setSaveErrors("Unexpected Exception occurred.");
      });
  };

  const renderDesktopNavBtns = () => {
    return (
      <>
        <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
          <Button sx={{ width: "90px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
            Cancel
          </Button>
        </Box>

        <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
          <Button
            sx={{
              width: "90px",
              backgroundColor: "secondary.swaGray",
              "&:hover": {
                backgroundColor: "secondary.swaGray5",
              },
            }}
            variant="contained"
            onClick={handleClickGuideDialogOpen}
          >
            Guide
          </Button>
        </Box>
        <Box sx={{ paddingTop: "10px" }}>
          <Button
            sx={{ width: "90px" }}
            variant="contained"
            color="primary"
            onClick={handleClickSubmitDialogOpen}
            disabled={!isDeclarationChanged() || isReadOnly}
          >
            Submit
          </Button>
        </Box>
      </>
    );
  };

  const renderDesktopDeclarationEmployeeForm = () => {
    if (isPageLoading) {
      return <PageLoading />;
    } else if (!hasAccessToViewPage) {
      return <div className="page-error">{NO_ACCESS_TO_PAGE_MESSAGE}</div>;
    }
    return (
      <>
        <StarsBreadcrumbs
          isConfirmationRequired={isDeclarationChanged()}
          breadcrumbsLinks={breadcrumbsLink}
          breadcrumbsList={employeeBreadcrumbsList}
          cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA}
        />
        <Grid container data-testid="iam-employee-declaration-form-desktop">
          <Grid container justifyContent="space-between">
            <Grid item sx={{ paddingLeft: "2rem", paddingTop: "10px" }}>
              <Grid item>
                <Typography component="div" align="left" sx={{ fontSize: "23px" }}>
                  {MY_DECLARATIONS_IAM_TITLE}
                </Typography>
                {/* Last submitted */}
                {(empDeclarationData?.createdBy === EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_ADMIN ||
                  empDeclarationData?.createdBy === EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_EMP) &&
                empDeclarationData?.createdAt &&
                lastUpdatedByFirstName &&
                lastUpdatedByLastName &&
                lastUpdatedByFirstName !== "" &&
                lastUpdatedByLastName !== "" ? (
                  <Grid className={classes.lastSubmitted}>
                    {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString(
                      empDeclarationData?.createdAt,
                      getTimeZoneFromSwaStation(userSession.homeStation)
                    )}`}
                  </Grid>
                ) : empDeclarationData ? (
                  <Grid className={classes.lastSubmitted}>{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString(
                    empDeclarationData?.createdAt,
                    getTimeZoneFromSwaStation(userSession.homeStation)
                  )}`}</Grid>
                ) : (
                  ""
                )}
                <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                  {declarationWindowDates[0] !== null &&
                    declarationWindowDates[1] !== null &&
                    parseInt(selectedYear) > 2025 && (
                      <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                        <DeclarationTimer
                          windowDates={[declarationWindowDates[0], declarationWindowDates[1]]}
                          station={userSession.homeStation}
                          selectedYear={selectedYear}
                        />
                      </Typography>
                    )}
                </Box>
              </Grid>
            </Grid>
            <Stack>
              <Grid item sx={{ display: "flex", paddingRight: "2rem" }}>
                {renderDesktopNavBtns()}
              </Grid>
            </Stack>
          </Grid>
          <Box
            sx={{
              width: "100%",
              margin: "auto",
              paddingLeft: "2rem",
              paddingRight: "2rem",
              marginTop: "10px",
            }}
          >
            <Grid container sx={{ marginTop: "15px", backgroundColor: "secondary.swaGray2" }} alignItems={"center"}>
              <Grid item sx={{ textAlign: "left", marginLeft: "30px", padding: "5px", fontWeight: "bold" }}>
                Declaration Round:
              </Grid>
              <Grid item sx={{ marginTop: "10px", marginBottom: "10px" }}>
                <StarsSelectInput
                  label="Declaration Year"
                  menuMessage="Select Year"
                  value={selectedYear}
                  allOptions={getDeclarationYearAllOptions()}
                  onChange={handleDeclarationYearChange}
                  errors={selectedYear === "" ? "Year Filter must be selected" : ""}
                  className={classes.filterInputs}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ backgroundColor: "secondary.swaGray1" }}>
              <Grid container sx={{ textAlign: "left", marginLeft: "25px", padding: "10px" }}>
                <div>
                  <b>Projected Balance: </b>
                  {getFormattedProjectedVacationBalance(IAM_CONTRACT, userSession.isFTOrPT, projectedVacationBalance)}
                </div>
              </Grid>
            </Grid>
          </Box>
          {isReadOnly ? (
            <Box
              sx={{
                width: "100%",
                margin: "auto",
                border: "solid 1px",
                borderColor: "secondary.swaGray2",
                marginLeft: "2rem",
                marginRight: "2rem",
              }}
            >
              <Grid container sx={{ paddingTop: "10px" }} alignItems={"center"}>
                <Grid
                  item
                  md={2}
                  sx={{
                    marginLeft: "25px",
                    padding: "10px",
                    fontWeight: "bold",
                    fontSize: "14px",
                    textAlign: "left",
                  }}
                >
                  DAT weeks:
                </Grid>
                <Grid item md={4} textAlign={"left"}>
                  {datWeeks === "" || datWeeks === undefined ? "-" : datWeeks}
                </Grid>
              </Grid>
              <Grid container sx={{ paddingTop: "20px" }} alignItems={"center"}>
                <Grid
                  item
                  md={2}
                  sx={{
                    marginLeft: "25px",
                    padding: "10px",
                    fontWeight: "bold",
                    fontSize: "14px",
                    textAlign: "left",
                  }}
                >
                  Block weeks:
                </Grid>
                <Grid item xs={4} textAlign={"left"}>
                  {blockWeeks === "" || blockWeeks === undefined ? "-" : blockWeeks}
                </Grid>
              </Grid>
              <Grid
                sx={{
                  marginTop: "10px",
                  marginLeft: "25px",
                  padding: "10px",
                  fontStyle: "italic",
                  textAlign: "left",
                }}
              >
                <Typography sx={{ fontSize: "11px" }}>
                  Employees who fail to bid vacation during the declaration round will automatically be given all DATs.
                </Typography>
                <Typography sx={{ fontSize: "11px", marginTop: "10px", marginBottom: "10px" }}>
                  Employees who declare block weeks but fail to bid or bid enough choices in the subsequent bidding
                  rounds will have their declared block weeks assigned by the Company once all bidding rounds are
                  completed.
                </Typography>
              </Grid>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                margin: "auto",
                marginLeft: "2rem",
                marginRight: "2rem",
                border: "solid 1px",
                borderColor: "secondary.swaGray2",
              }}
            >
              <Grid container sx={{ paddingTop: "10px" }} alignItems={"center"}>
                <Grid
                  item
                  md={2}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    textAlign: "left",
                    marginLeft: "25px",
                    padding: "10px",
                  }}
                >
                  Enter the # of DAT weeks (0-5):
                </Grid>
                <Grid item md={4} sx={{ display: "flex" }}>
                  <div className={classes.declarationInput}>
                    <TextField
                      type="text"
                      label="# of DAT weeks"
                      data-testid="choices-count-textField"
                      className={classes.inputText}
                      variant="outlined"
                      value={datWeeks}
                      onChange={handleDATWeeksChange}
                      onBlur={handleDATWeeksBlur}
                      onKeyUp={handleDATWeeksEnterKeyPressed}
                      inputProps={{ inputMode: "numeric", maxLength: 1 }}
                      error={datWeeksErrors !== ""}
                      helperText={datWeeksErrors}
                    />
                  </div>
                </Grid>
              </Grid>
              <Grid container sx={{ paddingTop: "10px" }} alignItems={"center"}>
                <Grid
                  item
                  md={2}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    textAlign: "left",
                    marginLeft: "25px",
                    padding: "10px",
                  }}
                >
                  Enter the # of Block weeks (0-5):
                </Grid>
                <Grid item md={4} sx={{ display: "flex" }}>
                  <div className={classes.declarationInput}>
                    <TextField
                      label="# of Block weeks"
                      type="text"
                      data-testid="choices-count-textField"
                      className={classes.inputText}
                      variant="outlined"
                      value={blockWeeks}
                      onChange={handleBlockWeeksChange}
                      onBlur={handleBlockWeeksBlur}
                      onKeyUp={handleBlockWeeksEnterKeyPressed}
                      inputProps={{ inputMode: "numeric", maxLength: 1 }}
                      error={blockWeeksErrors !== ""}
                      helperText={blockWeeksErrors}
                    />
                  </div>
                </Grid>
              </Grid>
              <Grid
                sx={{
                  marginTop: "10px",
                  marginLeft: "25px",
                  padding: "10px",
                  fontStyle: "italic",
                  textAlign: "left",
                }}
              >
                <Typography sx={{ fontSize: "11px" }}>
                  Employees who fail to bid vacation during the declaration round will automatically be given all DATs.
                </Typography>
                <Typography sx={{ fontSize: "11px", marginTop: "10px", marginBottom: "10px" }}>
                  Employees who declare block weeks but fail to bid or bid enough choices in the subsequent bidding
                  rounds will have their declared block weeks assigned by the Company once all bidding rounds are
                  completed.
                </Typography>
              </Grid>
            </Box>
          )}
        </Grid>

        {renderDialogs()}
      </>
    );
  };

  const handleClickGuideDialogOpen = () => {
    setIsGuideDialogOpen(true);
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickWarningDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsWarningDialogOpen(false);
  };

  const handleWarningSubmitClicked = () => {
    setSelectedYear(selectedYearRef.current);
    setDatWeeks("");
    setBlockWeeks("");
    setDatWeeksErrors("");
    setBlockWeeksErrors("");

    setEmpDeclarationData(null);
    setIsWarningDialogOpen(false);
  };

  const handleCloseClickedGuideDialog = () => {
    setIsGuideDialogOpen(false);
  };

  const renderGuideDialog = () => {
    return (
      <Dialog
        sx={{
          width: "650px",
          maxWidth: "100vw",
          margin: "auto",
          whiteSpace: "pre-line",
        }}
        open={isGuideDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        <IconButton
          aria-label="close"
          onClick={handleCloseClickedGuideDialog}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[650],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ marginTop: "25px", fontWeight: "bold" }}>Guide</DialogTitle>
        <DialogContent>
          <DialogContentText
            component={"div"}
            sx={{ fontSize: "14px", color: (theme) => theme.palette.secondary.swaGray5 }}
          >
            <ul>
              <li style={{ marginTop: "10px" }}>1 week = 5 DATs</li>
              <li style={{ marginTop: "10px" }}>
                If less than 5 days of vacation are accrued, DAT days must be taken.
              </li>
            </ul>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  };

  const renderWarningDialog = () => {
    return (
      <ConfirmationDialog
        isDialogOpen={isWarningDialogOpen}
        title="Warning"
        message={DECLARATION_YEAR_SELECTION_CHANGE}
        handleNoButtonClicked={handleClickWarningDialogClose}
        handleYesButtonClicked={handleWarningSubmitClicked}
      />
    );
  };

  const renderSubmitResponseDialog = () => {
    return (
      <SuccessDialog
        isSuccessDialogOpen={isSubmitResponseDialogOpen}
        title={saveErrors !== "" ? "Error" : "Changes Saved"}
        message={DECLARATION_SUCCESS_MESSAGE_DIALOG}
        hasError={saveErrors !== ""}
        error={DECLARATION_ERROR_MESSAGE_DIALOG}
        handleOKClick={() => {
          setIsSubmitResponseDialogOpen(false);
          fetchEmployeeDeclarationInfo(userSession.eid, selectedYear);
          if (pathNameHasYear(location.pathname)) {
            props.history.push(`/${FEATURES.MY_DECLARATIONS}`);
          }
        }}
      />
    );
  };

  const renderSubmitDeclarationDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        sx={{ margin: "auto" }}
        open={isSubmitDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        <DialogTitle>Submit Declaration</DialogTitle>
        <DialogContent>
          <DialogContentText component={"div"}>
            <Grid container>
              <div style={{ marginTop: "10px" }}>You are submitting your {selectedYear} Declaration.</div>
              <div style={{ marginTop: "10px" }}>Anything previously submitted will be overwritten.</div>
              <div style={{ marginTop: "10px" }}>
                You will see your submission next time you open this form again. You can keep editing until the round
                ends.
              </div>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ConfirmButton variant="outlined" color="primary" onClick={handleClickSubmitDialogClose}>
            Cancel
          </ConfirmButton>
          <ConfirmButton
            variant="contained"
            color="primary"
            onClick={handleSubmitClicked}
            isLoading={isSubmitLoading}
            disabled={isSubmitLoading || !isDeclarationChanged()}
          >
            Submit
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const renderDialogs = () => {
    return (
      <>
        <ConfirmationDialog
          isDialogOpen={isExitDialogOpen}
          title="Cancel"
          message={DECLARATION_FORM_CANCEL_MESSAGE}
          handleNoButtonClicked={handleClickCancelDialogClose}
          redirectTo={HOME_PATH}
        />
        {renderGuideDialog()}
        {renderSubmitDeclarationDialog()}
        {renderWarningDialog()}
        {renderSubmitResponseDialog()}
      </>
    );
  };

  const renderMobileNavBtnsForBidForm = () => {
    return (
      <>
        <Grid item xs={3} sx={{ padding: 1, display: "flex", justifyContent: "start", marginLeft: "10px" }}>
          <Button
            sx={{
              width: "100px",
              backgroundColor: "secondary.swaGray",
              "&:hover": {
                backgroundColor: "secondary.swaGray5",
              },
            }}
            variant="contained"
            onClick={handleClickGuideDialogOpen}
          >
            Guide
          </Button>
        </Grid>
        <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
          <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
            Cancel
          </Button>
          <Button
            sx={{ width: "100px", marginLeft: "10px", marginRight: "-10px" }}
            variant="contained"
            color="primary"
            onClick={handleClickSubmitDialogOpen}
            disabled={!isDeclarationChanged() || isReadOnly}
          >
            Submit
          </Button>
        </Grid>
      </>
    );
  };

  const renderMobileEmployeeDeclarationForm = () => {
    if (isPageLoading) {
      return <PageLoading />;
    } else if (!hasAccessToViewPage) {
      return <div className="page-error">{NO_ACCESS_TO_PAGE_MESSAGE}</div>;
    }
    return (
      <div className={classes.declarationScroll} data-testid="iam-employee-declaration-form-mobile">
        {/* Title */}
        <Grid container justifyContent="space-between">
          <Grid item sx={{ paddingLeft: "1rem", paddingTop: "20px" }}>
            <Typography variant="h6" component="div" align="left">
              {MY_DECLARATIONS_IAM_TITLE}
            </Typography>
            {/* Last Submitted */}
            {(empDeclarationData?.createdBy === EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_ADMIN ||
              empDeclarationData?.createdBy === EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_EMP) &&
            empDeclarationData?.createdAt &&
            lastUpdatedByFirstName &&
            lastUpdatedByLastName &&
            lastUpdatedByFirstName !== "" &&
            lastUpdatedByLastName !== "" ? (
              <Grid className={classes.lastSubmitted}>
                {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString(
                  empDeclarationData?.createdAt,
                  getTimeZoneFromSwaStation(userSession.homeStation)
                )}`}
              </Grid>
            ) : empDeclarationData ? (
              <Grid className={classes.lastSubmitted}>{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString(
                empDeclarationData?.createdAt,
                getTimeZoneFromSwaStation(userSession.homeStation)
              )}`}</Grid>
            ) : (
              ""
            )}
            <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
              {declarationWindowDates[0] !== null &&
                declarationWindowDates[1] !== null &&
                parseInt(selectedYear) > 2025 && (
                  <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "12px" }}>
                    <Grid container spacing={0.5}>
                      <Grid item>
                        <AccessTimeIcon fontSize="12px" />
                      </Grid>
                      <Grid item xs={11.2} sm={11.2} style={{ fontWeight: "bold", fontSize: "12px" }}>
                        <DeclarationTimer
                          windowDates={[declarationWindowDates[0], declarationWindowDates[1]]}
                          station={userSession.homeStation}
                          selectedYear={selectedYear}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                )}
            </Box>
          </Grid>
        </Grid>
        <Grid container sx={{ backgroundColor: "secondary.swaGray2", marginTop: "15px" }} alignItems={"center"}>
          <Grid item sx={{ textAlign: "left", marginLeft: "15px", padding: "5px", fontWeight: "bold" }}>
            Declaration Round:
          </Grid>
          <Grid item sx={{ marginTop: "10px", marginBottom: "10px" }}>
            <StarsSelectInput
              label="Declaration Year"
              menuMessage="Select Year"
              value={selectedYear}
              allOptions={getDeclarationYearAllOptions()}
              onChange={handleDeclarationYearChange}
              errors={selectedYear === "" ? "Year Filter must be selected" : ""}
              className={classes.filterInputs}
            />
          </Grid>
        </Grid>
        <Grid container sx={{ backgroundColor: "secondary.swaGray1" }}>
          <Grid item sx={{ textAlign: "left", marginLeft: "10px", padding: "10px" }}>
            <div>
              <b>Projected Balance: </b>
              {getFormattedProjectedVacationBalance(IAM_CONTRACT, userSession.isFTOrPT, projectedVacationBalance)}
            </div>
          </Grid>
        </Grid>

        <Box sx={{ width: "100%", margin: "auto", marginTop: "10px" }}>
          {isReadOnly ? (
            <>
              <Grid container sx={{ paddingTop: "10px" }} alignItems={"center"}>
                <Grid
                  item
                  xs={5}
                  sx={{
                    paddingLeft: "3rem",
                    fontWeight: "bold",
                    fontSize: "14px",
                    textAlign: "left",
                  }}
                >
                  DAT weeks:
                </Grid>
                <Grid item xs={7} textAlign={"left"}>
                  {datWeeks !== "" ? datWeeks : "-"}
                </Grid>
              </Grid>
              <Grid container sx={{ paddingTop: "10px", marginBlock: "10px" }} alignItems={"center"}>
                <Grid
                  item
                  xs={5}
                  sx={{
                    paddingLeft: "3rem",
                    fontWeight: "bold",
                    fontSize: "14px",
                    textAlign: "left",
                  }}
                >
                  Block weeks:
                </Grid>
                <Grid item xs={7} textAlign={"left"}>
                  {blockWeeks !== "" ? blockWeeks : "-"}
                </Grid>
              </Grid>
              <Grid
                style={{
                  marginTop: "20px",
                  paddingLeft: "3.5rem",
                  paddingRight: "3.5rem",
                  fontStyle: "italic",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "11px" }}>
                  Employees who fail to submit a declaration round bid will be awarded vacation as DAT days only.
                </Typography>
                <Typography sx={{ fontSize: "11px", marginTop: "10px", marginBottom: "30px" }}>
                  Employees who bid blocked weeks and fail to bid, or who do not bid enough weeks after all rounds are
                  completed, will have their declared vacation weeks assigned by the Company.
                </Typography>
              </Grid>
            </>
          ) : (
            <>
              <Grid container sx={{ paddingTop: "10px" }}>
                <Grid
                  item
                  xs={6}
                  sx={{
                    paddingLeft: "3rem",
                    fontWeight: "bold",
                    fontSize: "14px",
                    textAlign: "left",
                    marginTop: "10px",
                  }}
                >
                  Enter the # of DAT weeks (0-5):
                </Grid>
                <Grid item xs={6} sx={{ display: "flex" }}>
                  <div className={classes.declarationInput}>
                    <TextField
                      type="text"
                      label="# of DAT weeks"
                      className={classes.inputText}
                      variant="outlined"
                      value={datWeeks}
                      onChange={handleDATWeeksChange}
                      onBlur={handleDATWeeksBlur}
                      onKeyUp={handleDATWeeksEnterKeyPressed}
                      inputProps={{ inputMode: "numeric", maxLength: 1 }}
                      error={datWeeksErrors !== ""}
                      helperText={datWeeksErrors}
                    />
                  </div>
                </Grid>
              </Grid>
              <Grid container sx={{ paddingTop: "10px" }}>
                <Grid
                  item
                  xs={6}
                  sx={{
                    paddingLeft: "3rem",
                    fontWeight: "bold",
                    fontSize: "14px",
                    textAlign: "left",
                    marginTop: "10px",
                  }}
                >
                  Enter the # of Block weeks (0-5):
                </Grid>
                <Grid item xs={6} sx={{ display: "flex" }}>
                  <div className={classes.declarationInput}>
                    <TextField
                      label="# of Block weeks"
                      type="text"
                      data-testid="choices-count-textField"
                      className={classes.inputText}
                      variant="outlined"
                      value={blockWeeks}
                      onChange={handleBlockWeeksChange}
                      onBlur={handleBlockWeeksBlur}
                      onKeyUp={handleBlockWeeksEnterKeyPressed}
                      inputProps={{ inputMode: "numeric", maxLength: 1 }}
                      error={blockWeeksErrors !== ""}
                      helperText={blockWeeksErrors}
                    />
                  </div>
                </Grid>
              </Grid>

              <Grid
                style={{
                  marginTop: "20px",
                  paddingLeft: "3.5rem",
                  paddingRight: "3.5rem",
                  fontStyle: "italic",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "11px" }}>
                  Employees who fail to submit a declaration round bid will be awarded vacation as DAT days only.
                </Typography>
                <Typography sx={{ fontSize: "11px", marginTop: "10px", marginBottom: "30px" }}>
                  Employees who bid blocked weeks and fail to bid, or who do not bid enough weeks after all rounds are
                  completed, will have their declared vacation weeks assigned by the Company.
                </Typography>
              </Grid>
            </>
          )}
        </Box>
        <Divider />

        <Grid
          container
          sx={{
            marginTop: "5px",
            bottom: 0,
            position: "fixed",
            backgroundColor: "secondary.swaWhite",
            zIndex: 999,
          }}
        >
          {renderMobileNavBtnsForBidForm()}
        </Grid>
        {renderDialogs()}
      </div>
    );
  };

  return (
    <div className="App-contents" data-testid="iam-employee-declarations-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopDeclarationEmployeeForm() : renderMobileEmployeeDeclarationForm()}</>
      )}
    </div>
  );
};

export default MyDeclarationsPageIam;
